/**
 * Usage Indicator - Shows AI query usage in the header bar.
 * Clicking it opens a dropdown with all quota details.
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api/client';
import { useAuthStore } from '@/store/authStore';
import {
  Sparkles, FileText, Users, HardDrive, Building2, Link2,
  ChevronDown, ArrowUpRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface UsageItem {
  used: number;
  max: number;
  percent: number;
}

function formatStorage(bytes: number): string {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(0)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function UsageRow({ icon: Icon, label, item, format }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  item: UsageItem;
  format?: 'storage';
}) {
  const used = format === 'storage' ? formatStorage(item.used) : item.used.toLocaleString();
  const max = format === 'storage' ? formatStorage(item.max) : item.max.toLocaleString();
  const color = item.percent >= 90 ? 'bg-red-500' : item.percent >= 70 ? 'bg-amber-500' : 'bg-[#5048e5]';

  return (
    <div className="py-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
        <span className="font-medium text-foreground">{used} / {max}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-500', color)}
          style={{ width: `${Math.min(item.percent, 100)}%` }}
        />
      </div>
    </div>
  );
}

export function UsageIndicator() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuthStore();

  // Don't show for super admin
  if (isSuperAdmin()) return null;

  const { data } = useQuery({
    queryKey: ['billing-usage'],
    queryFn: async () => {
      const r = await apiClient.get('/subscription/billing');
      return r.data?.data;
    },
    staleTime: 60000, // 1 minute
    refetchInterval: 120000, // 2 minutes
  });

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!data?.usage) return null;

  const ai = data.usage.aiQueries as UsageItem;
  const planName = data.plan?.planDisplayName || data.plan?.planName || 'Free';
  const aiColor = ai.percent >= 90 ? 'text-red-500' : ai.percent >= 70 ? 'text-amber-500' : 'text-[#5048e5]';
  const aiBgColor = ai.percent >= 90 ? 'bg-red-500' : ai.percent >= 70 ? 'bg-amber-500' : 'bg-[#5048e5]';

  return (
    <div ref={ref} className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-muted/60 transition text-xs"
      >
        <Sparkles className={cn('h-3.5 w-3.5', aiColor)} />
        <span className="text-muted-foreground">
          <span className={cn('font-semibold', aiColor)}>{ai.used}</span>
          <span className="text-muted-foreground/60">/{ai.max}</span>
        </span>
        {/* Mini progress bar */}
        <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full', aiBgColor)}
            style={{ width: `${Math.min(ai.percent, 100)}%` }}
          />
        </div>
        <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-popover border rounded-lg shadow-lg z-50 p-4">
          {/* Plan header */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b">
            <div>
              <span className="text-xs text-muted-foreground">Current Plan</span>
              <p className="text-sm font-semibold">{planName}</p>
            </div>
            <button
              onClick={() => { setOpen(false); navigate('/panel/upgrade'); }}
              className="text-[10px] text-[#5048e5] hover:underline flex items-center gap-0.5"
            >
              Upgrade <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>

          {/* Usage rows */}
          <div className="space-y-1">
            <UsageRow icon={Sparkles} label="AI Queries" item={data.usage.aiQueries} />
            <UsageRow icon={FileText} label="Documents" item={data.usage.documents} />
            <UsageRow icon={Users} label="Users" item={data.usage.users} />
            <UsageRow icon={Building2} label="Clients" item={data.usage.clients} />
            <UsageRow icon={HardDrive} label="Storage" item={data.usage.storage} format="storage" />
            <UsageRow icon={Link2} label="Xero Connections" item={data.usage.xero} />
          </div>

          {/* Footer */}
          <div className="mt-3 pt-3 border-t">
            <button
              onClick={() => { setOpen(false); navigate('/panel/billing'); }}
              className="text-xs text-muted-foreground hover:text-foreground transition w-full text-left"
            >
              View Billing & Plan Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
